// Version 1.0.1 - Force fresh compilation
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCSVStore, UploadStep } from '../stores/csvStore';
import { CSVFile } from '../types/data/csv';
import { FileUploader } from '../components/csv/FileUploader';
import CSVPreview from '../components/csv/CSVPreview';
import ValidationResults from '../components/csv/ValidationResults';
import Sidebar from '../components/shared/Sidebar';
import AIAnalysisButton from '../components/csv/AIAnalysisButton';
import ReportPopup from '../components/csv/ReportPopup';
import LoadingPopup from '../components/csv/LoadingPopup';
import './CSVUploadWithQuestionsPage.css';
import { analyzeData } from '../services/bedrockApi';
import KPITable from '../components/csv/KPITable';
import csvService from '../services/csvService';
import UserQuestionService from '../services/userQuestionService';
import useAuthStore from '../store/authStore';
import { UserQuestion } from '../types/UserQuestion';
import { 
  TableContainer, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Paper 
} from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Map the enum steps to numeric indices for the UI
const UPLOAD = 0;
const PREVIEW = 1;
const MAP = 2;
const VALIDATE = 3;

// Claude 4.5 Sonnet ARN model ID (primary model)
const CLAUDE_45_SONNET_MODEL_ID = 'arn:aws:bedrock:eu-central-1:586794487615:inference-profile/eu.anthropic.claude-sonnet-4-5-20250929-v1:0';

// Claude 3.7 Sonnet ARN model ID (fallback model)
const CLAUDE_37_SONNET_MODEL_ID = 'arn:aws:bedrock:eu-central-1::inference-profile/eu.anthropic.claude-3-7-sonnet-20250219-v1:0';

// Nova Pro model ID for mapping
const NOVA_PRO_MODEL_ID = 'arn:aws:bedrock:eu-central-1:586794487615:inference-profile/eu.amazon.nova-pro-v1:0';

// Custom prompt for Map with AI step
const MAP_WITH_AI_PROMPT = `You are a financial automotive accountant. Can you analyse every Field_Code and Description in the file and figure out what values they represent. I want you to return a table of field codes and the real english name you have assigned it.`;

// Update the KPI analysis prompt
const KPI_ANALYSIS_PROMPT = `Analyze the CSV data and identify at least 200 important KPI columns.
Return ONLY a JSON array of objects, each with the exact column name as 'code' and a simple English description as 'description'.
Format: [{"code": "KPI1", "description": "Gross Profit"}, ...]
Rules:
1. Return ONLY the array, no other text
2. Use exact column names from the CSV as 'code'
3. 'description' should be a simple, human-friendly name for the KPI
4. Include at least 200 KPIs where possible, but never less than 10
5. Keep response minimal - just the array`;

// Helper type for new KPI analysis format
interface KPIAnalysisObj {
  code: string;
  description: string;
}

// Add function to extract and store unique dealers and KPIs
const extractAndStoreUniqueData = async (filteredData: string[][], kpiAnalysis: KPIAnalysisObj[]) => {
  try {
    if (!filteredData || filteredData.length < 2) {
      console.log('No filtered data available for unique data extraction');
      return;
    }

    // Extract dealer names from the first column (assuming dealer names are in the first column)
    const dealerNames = new Set<string>();
    
    // Skip the header row and extract unique dealer names
    for (let i = 1; i < filteredData.length; i++) {
      const dealerName = filteredData[i][0]?.trim();
      if (dealerName && dealerName !== '') {
        dealerNames.add(dealerName);
      }
    }

    // Extract KPI names from the analysis
    const kpiNames = kpiAnalysis.map(kpi => kpi.code).filter(code => code && code.trim() !== '');

    console.log('Extracted unique data:', {
      dealerCount: dealerNames.size,
      kpiCount: kpiNames.length,
      dealers: Array.from(dealerNames).slice(0, 5), // Log first 5 for debugging
      kpis: kpiNames.slice(0, 5) // Log first 5 for debugging
    });

    // Send dealers to backend if any found
    if (dealerNames.size > 0) {
      try {
        const dealersResponse = await fetch(`${API_BASE_URL}/unique-data/dealers/bulk`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            dealerNames: Array.from(dealerNames)
          })
        });

        if (dealersResponse.ok) {
          const dealersResult = await dealersResponse.json();
          console.log('Dealers stored successfully:', dealersResult);
        } else {
          console.warn('Failed to store dealers:', await dealersResponse.text());
        }
      } catch (dealersError) {
        console.error('Error storing dealers:', dealersError);
      }
    }

    // Send KPIs to backend if any found
    if (kpiNames.length > 0) {
      try {
        const kpisResponse = await fetch(`${API_BASE_URL}/unique-data/kpis/bulk`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            kpiNames: kpiNames
          })
        });

        if (kpisResponse.ok) {
          const kpisResult = await kpisResponse.json();
          console.log('KPIs stored successfully:', kpisResult);
        } else {
          console.warn('Failed to store KPIs:', await kpisResponse.text());
        }
      } catch (kpisError) {
        console.error('Error storing KPIs:', kpisError);
      }
    }

  } catch (error) {
    console.error('Error in extractAndStoreUniqueData:', error);
  }
};

interface CustomAIAnalysisButtonProps {
  prompt?: string;
  fileRef: string | null;
  modelId?: string;
  disabled?: boolean;
  filteredContent: string;
  isManuallyMapped?: boolean;
}

// Add a function to save filtered data as a file
const saveFilteredDataAsFile = async (content: string): Promise<string> => {
  try {
    const response = await fetch(`${API_BASE_URL}/bedrock/save-filtered-data`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ content })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Server response:', errorData);
      throw new Error(errorData.error || 'Failed to save filtered data');
    }

    const data = await response.json();
    return data.fileRef;
  } catch (err: any) {
    console.error('Error saving filtered data:', err);
    throw new Error('Failed to save filtered data for analysis');
  }
};

// Update the CustomAIAnalysisButton component to use filtered data consistently
const CustomAIAnalysisButton: React.FC<CustomAIAnalysisButtonProps> = ({ 
  prompt, 
  fileRef, 
  modelId, 
  disabled,
  filteredContent,
  isManuallyMapped
}) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);
  const [analysisFileRef, setAnalysisFileRef] = useState<string | null>(null);
  const { user } = useAuthStore(); // Get the current user from auth store

  const openReport = () => setIsReportOpen(true);
  const closeReport = () => setIsReportOpen(false);

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    setError(null);
    setAnalysisResult(null);
    setUsedFallback(false);

    try {
      if (!filteredContent) {
        throw new Error('No filtered data available. Please run KPI analysis first.');
      }

      // Always use filtered data in step 4 for consistent analysis
      const lines = filteredContent.split('\n');
      console.log('Sending filtered data for analysis:', {
        totalLines: lines.length,
        headers: lines[0],
        sampleRow: lines[1],
        totalColumns: lines[0].split(',').length,
        contentSize: filteredContent.length
      });

      // Save the filtered data as a file and get the file reference
      const filteredFileRef = await saveFilteredDataAsFile(filteredContent);
      console.log('Saved filtered data with fileRef:', filteredFileRef);
      setAnalysisFileRef(filteredFileRef);

      // Function to make the analysis request
      const makeAnalysisRequest = async (useModelId: string) => {
        // Create request body with appropriate parameters for each model
        const requestBody: any = {
          prompt,
          modelId: useModelId,
          fileRef: filteredFileRef,
          useFilteredData: true, // Always use filtered data in step 4
          requestType: 'csv_upload_questions' // Specify this is from CSV upload with questions
        };
        
        // Add max_tokens parameter for Claude 3.7 Sonnet
        if (useModelId === CLAUDE_37_SONNET_MODEL_ID) {
          requestBody.inference_config = {
            max_tokens: 400000 // Configure Claude 3.7 to use up to 400k tokens for larger outputs
          };
        }
        
        const response = await fetch(`${API_BASE_URL}/bedrock/analyze/file`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Error response:', errorData);
          throw new Error(errorData.error || 'Failed to analyze data');
        }

        const data = await response.json();
        return data.result;
      };

      // First try with Claude 4.0 Sonnet
      try {
        console.log('Trying to analyze with Claude 4.0 Sonnet...');
        const result = await makeAnalysisRequest(CLAUDE_45_SONNET_MODEL_ID);
        console.log('Analysis successful with Claude 4.0 Sonnet');
        setAnalysisResult(result);
        logAIUsage('csv_questions_analysis', 'claude_4.0_sonnet', 'success');
      } catch (error) {
        console.error('Error analyzing with Claude 4.0 Sonnet, falling back to Claude 3.7:', error);
        
        // Log the failed attempt
        logAIUsage('csv_questions_analysis', 'claude_4.0_sonnet', 'error');
        
        try {
          // Fallback to Claude 3.7 Sonnet
          console.log('Falling back to Claude 3.7 Sonnet...');
          setUsedFallback(true);
          const fallbackResult = await makeAnalysisRequest(CLAUDE_37_SONNET_MODEL_ID);
          console.log('Analysis successful with fallback model Claude 3.7 Sonnet');
          setAnalysisResult(fallbackResult);
          logAIUsage('csv_questions_analysis', 'claude_3.7_sonnet', 'success');
        } catch (fallbackError) {
          console.error('Fallback analysis also failed:', fallbackError);
          logAIUsage('csv_questions_analysis', 'claude_3.7_sonnet', 'error');
          throw fallbackError;
        }
      }

      // Open the report on successful analysis
      openReport();
      
    } catch (error: any) {
      console.error('Final analysis error:', error);
      setError(error.message || 'An error occurred during analysis');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const logAIUsage = async (requestType: string, model: string, status: 'success' | 'error') => {
    try {
      await fetch(`${API_BASE_URL}/ai-usage/log`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          requestType, 
          model, 
          status,
          fileRef: analysisFileRef 
        })
      });
    } catch (error) {
      console.error('Failed to log AI usage:', error);
    }
  };

  return (
    <div className="ai-analysis-container">
      <button
        className={`primary-button ${disabled ? 'disabled' : ''}`}
        onClick={handleAnalyze}
        disabled={disabled || isAnalyzing}
      >
        {isAnalyzing ? 'Analyzing...' : 'Analyze with AI'}
      </button>
      
      {error && <div className="error-message">{error}</div>}
      
      {/* Loading popup when analyzing - show Space Invaders for admins */}
      <LoadingPopup 
        isOpen={isAnalyzing} 
        message={usedFallback 
          ? "Still working on analyzing your data... (Using fallback model)" 
          : "Analyzing your financial data with AI..."}
        isAdmin={user?.role === 'admin'}
      />
      
      {analysisResult && (
        <ReportPopup
          isOpen={isReportOpen}
          onClose={closeReport}
          analysisResult={analysisResult}
          fileName={`AI_Analysis_Report${usedFallback ? '_Fallback' : ''}`}
        />
      )}
    </div>
  );
};

// Helper function to log KPI mapping debug information
const logKPIMappingDebug = (
  kpiAnalysis: KPIAnalysisObj[],
  kpiIdentifiers: string[],
  matched: boolean = false
) => {
  // If we have too many KPIs to log, just log a sample
  if (kpiAnalysis.length > 10) {
    console.log(`KPI Analysis sample (${matched ? 'matched' : 'unmatched'}):`);
    console.log('First 5 KPI codes:', kpiAnalysis.slice(0, 5).map(k => `"${k.code}"`).join(', '));
    console.log('Last 5 KPI codes:', kpiAnalysis.slice(-5).map(k => `"${k.code}"`).join(', '));
  } else {
    console.log(`All KPI codes (${matched ? 'matched' : 'unmatched'}):`);
    console.log(kpiAnalysis.map(k => `"${k.code}"`).join(', '));
  }
  
  if (kpiIdentifiers.length > 0) {
    console.log(`KPI identifiers ${matched ? 'matched' : 'to match'}:`);
    if (kpiIdentifiers.length > 10) {
      console.log('Sample KPI identifiers:', kpiIdentifiers.slice(0, 10).map(id => `"${id}"`).join(', '));
    } else {
      console.log(kpiIdentifiers.map(id => `"${id}"`).join(', '));
    }
  }
};

// Update the createFilteredCSVData function to properly match KPI codes
const createFilteredCSVData = (
  data: string[][],
  kpiAnalysis: KPIAnalysisObj[]
) => {
  if (!data || data.length === 0) return { headers: [], rows: [] };

  const allHeaders = data[0];
  console.log('Original headers:', allHeaders);
  
  // Find ALL relevant columns - including both KPI Description and KPI Name
  let dealerNameIndex = allHeaders.findIndex(h => {
    const header = h.toString().trim().toLowerCase();
    return header === 'dealer name' || 
           header === 'name' || 
           header === 'dealername' || 
           header === 'dealer' ||
           header.includes('dealer') && header.includes('name');
  });
  
  let yearIndex = allHeaders.findIndex(h => {
    const header = h.toString().trim().toLowerCase();
    return header === 'year' || 
           header === 'dsd_year' || 
           header === 'yr' || 
           header === 'cal year' ||
           header.includes('year');
  });
  
  let monthIndex = allHeaders.findIndex(h => {
    const header = h.toString().trim().toLowerCase();
    return header === 'month' || 
           header === 'dsd_month' || 
           header === 'mo' || 
           header === 'cal month' ||
           header.includes('month');
  });
  
  // Find both KPI descriptor columns
  let kpiDescIndex = allHeaders.findIndex(h => {
    const header = h.toString().trim().toLowerCase();
    return header === 'kpi description' || 
           header === 'kpidescription' || 
           header === 'kpi desc' || 
           header === 'kpi_description' ||
           (header.includes('kpi') && header.includes('desc'));
  });
  
  let kpiNameIndex = allHeaders.findIndex(h => {
    const header = h.toString().trim().toLowerCase();
    return header === 'kpi name' || 
           header === 'kpiname' || 
           header === 'kpi_name' || 
           header === 'kpi' ||
           (header.includes('kpi') && header.includes('name')) ||
           header === 'field_code';
  });
  
  let valueIndex = allHeaders.findIndex(h => {
    const header = h.toString().trim().toLowerCase();
    return header === 'calendar ytd' || 
           header === 'field_value' || 
           header === 'value' || 
           header === 'ytd' || 
           header === 'cal ytd' ||
           header.includes('value') ||
           header.includes('ytd');
  });

  // Log all found column indices
  console.log('Column indices found:', {
    dealerNameIndex,
    yearIndex,
    monthIndex,
    kpiDescIndex,
    kpiNameIndex,
    valueIndex
  });

  // If we couldn't find critical columns, try a more aggressive fallback approach
  if (dealerNameIndex === -1 || yearIndex === -1 || monthIndex === -1 || 
      (kpiDescIndex === -1 && kpiNameIndex === -1) || valueIndex === -1) {
    
    console.log('Some required columns not found. Trying fallback detection...');
    
    // Fallback for Dealer Name
    if (dealerNameIndex === -1) {
      const possibleDealerColumns = allHeaders.map((h, idx) => {
        const header = h.toString().toLowerCase();
        // Look for any column that might contain dealer info
        if (header.includes('dealer') || 
            header.includes('client') || 
            header.includes('customer') || 
            header.includes('company')) {
          return { index: idx, header: h };
        }
        return null;
      }).filter(Boolean);
      
      if (possibleDealerColumns.length > 0) {
        console.log('Possible dealer name columns:', possibleDealerColumns);
        dealerNameIndex = possibleDealerColumns[0]?.index ?? -1;
      }
    }
    
    // Fallback for Year
    if (yearIndex === -1) {
      const possibleYearColumns = allHeaders.map((h, idx) => {
        const header = h.toString().toLowerCase();
        // Look for any column that might contain year info
        if (header.includes('year') || 
            header.includes('yr') || 
            header.includes('date') && !header.includes('month')) {
          return { index: idx, header: h };
        }
        return null;
      }).filter(Boolean);
      
      if (possibleYearColumns.length > 0) {
        console.log('Possible year columns:', possibleYearColumns);
        yearIndex = possibleYearColumns[0]?.index ?? -1;
      }
    }
    
    // Fallback for Month
    if (monthIndex === -1) {
      const possibleMonthColumns = allHeaders.map((h, idx) => {
        const header = h.toString().toLowerCase();
        // Look for any column that might contain month info
        if (header.includes('month') || 
            header.includes('mo') || 
            header.includes('date') && !header.includes('year')) {
          return { index: idx, header: h };
        }
        return null;
      }).filter(Boolean);
      
      if (possibleMonthColumns.length > 0) {
        console.log('Possible month columns:', possibleMonthColumns);
        monthIndex = possibleMonthColumns[0]?.index ?? -1;
      }
    }
    
    // Fallback for KPI columns
    if (kpiDescIndex === -1 && kpiNameIndex === -1) {
      const possibleKpiColumns = allHeaders.map((h, idx) => {
        const header = h.toString().toLowerCase();
        // Look for any column that might contain KPI info
        if (header.includes('kpi') || 
            header.includes('metric') || 
            header.includes('measure') ||
            header.includes('indicator') ||
            header.includes('field_code') ||
            header.includes('field code')) {
          return { index: idx, header: h };
        }
        return null;
      }).filter(Boolean);
      
      if (possibleKpiColumns.length > 0) {
        console.log('Possible KPI columns:', possibleKpiColumns);
        if (kpiDescIndex === -1) {
          kpiDescIndex = possibleKpiColumns[0]?.index ?? -1;
        }
        if (kpiNameIndex === -1 && possibleKpiColumns.length > 1) {
          kpiNameIndex = possibleKpiColumns[1]?.index ?? -1;
        }
      }
    }
    
    // Fallback for Value
    if (valueIndex === -1) {
      const possibleValueColumns = allHeaders.map((h, idx) => {
        const header = h.toString().toLowerCase();
        // Look for any column that might contain value info
        if (header.includes('value') || 
            header.includes('amount') || 
            header.includes('total') ||
            header.includes('ytd') ||
            header.includes('sum') ||
            header.includes('qty') ||
            header.includes('figure')) {
          return { index: idx, header: h };
        }
        return null;
      }).filter(Boolean);
      
      if (possibleValueColumns.length > 0) {
        console.log('Possible value columns:', possibleValueColumns);
        valueIndex = possibleValueColumns[0]?.index ?? -1;
      }
    }
    
    // Log the results of fallback detection
    console.log('Fallback column detection results:', {
      dealerNameIndex,
      yearIndex,
      monthIndex,
      kpiDescIndex,
      kpiNameIndex,
      valueIndex
    });
  }

  // Check if required columns are missing and provide a detailed error
  const missingColumns = [];
  if (dealerNameIndex === -1) missingColumns.push('Dealer Name/Name');
  if (yearIndex === -1) missingColumns.push('Year/dsd_year');
  if (monthIndex === -1) missingColumns.push('Month/dsd_month');
  if (kpiDescIndex === -1 && kpiNameIndex === -1) missingColumns.push('KPI Description or KPI Name');
  if (valueIndex === -1) missingColumns.push('Calendar YTD/Field_Value');

  if (missingColumns.length > 0) {
    // Log headers for debugging
    console.log('Headers found in CSV:', allHeaders);
    console.log('Missing required columns:', missingColumns);
    
    // Create a more detailed error message
    let errorMsg = `Required columns not found in the data: ${missingColumns.join(', ')}`;
    
    // Add information about what headers were actually found
    if (allHeaders.length > 0) {
      errorMsg += `\nHeaders found: ${allHeaders.slice(0, 10).join(', ')}${allHeaders.length > 10 ? '...' : ''}`;
    }
    
    // Add examples of expected data format
    errorMsg += `\n\nExpected CSV format example:
Dealer Name,Year,Month,KPI Name,Field_Value
ABC Motors,2023,1,Total Sales,1234.56
ABC Motors,2023,1,Gross Profit,234.56
XYZ Dealers,2023,1,Total Sales,2345.67
`;
    
    throw new Error(errorMsg);
  }

  // Build header row: Dealer Name, Year, Month, ...KPI Descriptions
  const newHeaders = ['Dealer Name', 'Year', 'Month', ...kpiAnalysis.map(kpi => kpi.description)];
  
  // Log KPI analysis and debug information
  console.log('KPI Analysis for filtering:');
  console.log(`- Total KPIs provided: ${kpiAnalysis.length}`);
  
  // Debug: Log some KPI code samples for diagnostic purposes
  logKPIMappingDebug(kpiAnalysis, []);
  
  // Collect all unique KPI identifiers from the data for matching analysis
  let uniqueKpiIdentifiers = new Set<string>();
  for (let i = 1; i < data.length; i++) {
    const row = data[i];
    if (row.length <= Math.max(kpiDescIndex !== -1 ? kpiDescIndex : 0, kpiNameIndex !== -1 ? kpiNameIndex : 0)) continue;
    
    const kpiDesc = kpiDescIndex !== -1 ? (row[kpiDescIndex]?.trim() || '') : '';
    const kpiName = kpiNameIndex !== -1 ? (row[kpiNameIndex]?.trim() || '') : '';
    
    if (kpiDesc) uniqueKpiIdentifiers.add(kpiDesc);
    if (kpiName) uniqueKpiIdentifiers.add(kpiName);
  }
  
  console.log(`- Found ${uniqueKpiIdentifiers.size} unique KPI identifiers in the data`);
  
  // Step 1: Find all unique dealer-year-month combinations
  const dealerYearMonthMap = new Map<string, {
    dealer: string,
    year: string,
    month: string,
    kpis: Record<string, string>
  }>();
  
  // First collect all unique dealer-year-month combinations
  for (let i = 1; i < data.length; i++) {
    const row = data[i];
    if (row.length <= Math.max(dealerNameIndex, yearIndex, monthIndex)) continue;
    
    const dealer = row[dealerNameIndex]?.trim() || '';
    const year = row[yearIndex]?.trim() || '';
    const month = row[monthIndex]?.trim() || '';
    
    if (!dealer || !year || !month) continue;
    
    const key = `${dealer}|||${year}|||${month}`;
    if (!dealerYearMonthMap.has(key)) {
      dealerYearMonthMap.set(key, {
        dealer,
        year, 
        month,
        kpis: {}
      });
    }
  }
  
  console.log(`Found ${dealerYearMonthMap.size} unique dealer-year-month combinations`);

  // Log KPI analysis for debugging
  console.log(`KPI Analysis overview:
  - Total KPIs analyzed: ${kpiAnalysis.length}
  - First 5 KPI codes: ${kpiAnalysis.slice(0, 5).map(k => k.code).join(', ')}
  `);

  // Step 2: Now process each row to populate the KPI values
  let totalProcessedRows = 0;
  let matchedKpis = 0;
  let unmatchedKpis = 0;
  const unmatchedKpiSamples = new Set<string>();
  const matchedKpiCodes = new Set<string>();
  
  // Create a map of KPI codes to their objects for faster lookup
  const kpiCodeMap = new Map<string, KPIAnalysisObj>();
  kpiAnalysis.forEach(kpi => {
    kpiCodeMap.set(kpi.code.toLowerCase(), kpi);
  });
  
  for (let i = 1; i < data.length; i++) {
    const row = data[i];
    const maxColumnIndex = Math.max(
      dealerNameIndex, 
      yearIndex, 
      monthIndex, 
      kpiDescIndex !== -1 ? kpiDescIndex : 0,
      kpiNameIndex !== -1 ? kpiNameIndex : 0,
      valueIndex
    );
    
    if (row.length <= maxColumnIndex) {
      continue; // Skip rows that don't have enough columns
    }
    
    totalProcessedRows++;
    const dealer = row[dealerNameIndex]?.trim() || '';
    const year = row[yearIndex]?.trim() || '';
    const month = row[monthIndex]?.trim() || '';
    const value = row[valueIndex]?.trim() || '';
    
    // Get KPI identifiers from both possible columns
    const kpiDesc = kpiDescIndex !== -1 ? (row[kpiDescIndex]?.trim() || '') : '';
    const kpiName = kpiNameIndex !== -1 ? (row[kpiNameIndex]?.trim() || '') : '';
    
    if (!dealer || !year || !month || (!kpiDesc && !kpiName) || !value) continue;
    
    const key = `${dealer}|||${year}|||${month}`;
    if (!dealerYearMonthMap.has(key)) continue;
    
    // DIRECT MATCHING: Try to find a KPI object that matches our KPI identifiers using multiple methods
    let matchedKpi: KPIAnalysisObj | undefined = undefined;
    let matchMethod = ''; // For debugging: track how the match was found
    
    // 1. Try exact matches first (most reliable)
    if (!matchedKpi && kpiDesc) {
      matchedKpi = kpiAnalysis.find(k => k.code === kpiDesc);
      if (matchedKpi) matchMethod = 'exact-desc';
    }
    
    if (!matchedKpi && kpiName) {
      matchedKpi = kpiAnalysis.find(k => k.code === kpiName);
      if (matchedKpi) matchMethod = 'exact-name';
    }
    
    // 2. Try case-insensitive matching
    if (!matchedKpi && kpiDesc) {
      matchedKpi = kpiAnalysis.find(k => k.code.toLowerCase() === kpiDesc.toLowerCase());
      if (matchedKpi) matchMethod = 'case-insensitive-desc';
    }
    
    if (!matchedKpi && kpiName) {
      matchedKpi = kpiAnalysis.find(k => k.code.toLowerCase() === kpiName.toLowerCase());
      if (matchedKpi) matchMethod = 'case-insensitive-name';
    }
    
    // 3. Try matching with normalized strings (removing special chars)
    if (!matchedKpi) {
      // Normalize strings by removing special characters and extra spaces
      const normalize = (str: string) => {
        return str.toLowerCase()
          .replace(/[%().,]/g, '') // Remove special characters
          .replace(/\s+/g, ' ')    // Normalize spaces
          .trim();
      };
      
      const normalizedDesc = kpiDesc ? normalize(kpiDesc) : '';
      const normalizedName = kpiName ? normalize(kpiName) : '';
      
      // Try exact matches with normalized strings
      if (!matchedKpi && normalizedDesc) {
        matchedKpi = kpiAnalysis.find(k => normalize(k.code) === normalizedDesc);
        if (matchedKpi) matchMethod = 'normalized-exact-desc';
      }
      
      if (!matchedKpi && normalizedName) {
        matchedKpi = kpiAnalysis.find(k => normalize(k.code) === normalizedName);
        if (matchedKpi) matchMethod = 'normalized-exact-name';
      }
      
      // 4. Try substring matching (less reliable, but catches more)
      if (!matchedKpi) {
        // Check if KPI code contains or is contained by our identifiers
        matchedKpi = kpiAnalysis.find(k => {
          const normalizedCode = normalize(k.code);
          return (normalizedDesc && normalizedCode.includes(normalizedDesc)) ||
                 (normalizedName && normalizedCode.includes(normalizedName)) ||
                 (normalizedDesc && normalizedDesc.includes(normalizedCode)) ||
                 (normalizedName && normalizedName.includes(normalizedCode));
        });
        if (matchedKpi) matchMethod = 'substring';
      }
      
      // 5. Last resort: Try word matching (checking if any words match)
      if (!matchedKpi) {
        // Split into words and see if any significant words match
        const getSignificantWords = (str: string) => {
          // Filter out common words that aren't useful for matching
          const commonWords = ['and', 'or', 'the', 'of', 'in', 'for', 'to', 'with', 'by'];
          return str.split(/\s+/)
            .map(word => word.toLowerCase())
            .filter(word => word.length > 2 && !commonWords.includes(word));
        };
        
        const descWords = normalizedDesc ? getSignificantWords(normalizedDesc) : [];
        const nameWords = normalizedName ? getSignificantWords(normalizedName) : [];
        
        matchedKpi = kpiAnalysis.find(k => {
          const codeWords = getSignificantWords(k.code);
          // Check if any significant words match between our KPI and the identifier
          return codeWords.some(word => 
            descWords.includes(word) || nameWords.includes(word)
          );
        });
        if (matchedKpi) matchMethod = 'word-matching';
      }
    }
    
    // Log match results with method information
    if (!matchedKpi) {
      unmatchedKpis++;
      if (unmatchedKpiSamples.size < 10) {
        unmatchedKpiSamples.add(`Desc: "${kpiDesc}", Name: "${kpiName}"`);
      }
    } else {
      matchedKpis++;
      matchedKpiCodes.add(matchedKpi.code);
      
      // Update the KPI value in our map
      const entryData = dealerYearMonthMap.get(key);
      if (entryData) {
        entryData.kpis[matchedKpi.description] = value;
      }
      
      // Debug log periodically with match method info
      if (matchedKpis % 500 === 0) {
        console.log(`Match example (${matchMethod}): "${kpiDesc || kpiName}" => "${matchedKpi.code}" (${matchedKpi.description})`);
      }
    }
    
    // Log progress periodically
    if (totalProcessedRows % 1000 === 0) {
      console.log(`Processed ${totalProcessedRows} rows, matched ${matchedKpis} KPIs, unmatched ${unmatchedKpis} KPIs`);
    }
  }
  
  // Log KPI matching statistics
  console.log(`KPI Matching Stats:
  - Total rows processed: ${totalProcessedRows}
  - KPIs matched: ${matchedKpis}
  - KPIs unmatched: ${unmatchedKpis}
  - Match rate: ${(matchedKpis / (matchedKpis + unmatchedKpis) * 100).toFixed(2)}%
  - Unique KPI codes matched: ${matchedKpiCodes.size}/${kpiAnalysis.length} (${(matchedKpiCodes.size / kpiAnalysis.length * 100).toFixed(2)}%)
  `);
  
  if (unmatchedKpiSamples.size > 0) {
    console.log(`Unmatched KPI samples:`, Array.from(unmatchedKpiSamples));
  }
  
  // Check 3 random sample entries
  const sampleEntries = Array.from(dealerYearMonthMap.values())
    .filter(entry => Object.keys(entry.kpis).length > 0)
    .slice(0, 3);
  
  sampleEntries.forEach((entry, i) => {
    console.log(`Sample entry ${i+1}:
    - Dealer: ${entry.dealer}
    - Year: ${entry.year}
    - Month: ${entry.month}
    - KPI count: ${Object.keys(entry.kpis).length}
    - First few KPIs: ${JSON.stringify(Object.entries(entry.kpis).slice(0, 3))}
    `);
  });

  // Step 3: Convert the map to rows for output
  const processedRows: string[][] = [];
  processedRows.push(newHeaders);
  
  // Log dealer names for debugging
  const dealerNames = Array.from(dealerYearMonthMap.values()).map(entry => entry.dealer);
  console.log(`Dealer names (${dealerNames.length}):`, Array.from(new Set(dealerNames)).slice(0, 10));
  
  // Convert each dealer-year-month entry to a row
  // Use Array.from to properly convert the Map values to an array before iterating
  Array.from(dealerYearMonthMap.values()).forEach(entry => {
    const row: string[] = [entry.dealer, entry.year, entry.month];
    
    // Add each KPI value in the correct order
    kpiAnalysis.forEach(kpi => {
      row.push(entry.kpis[kpi.description] || '');
    });
    
    processedRows.push(row);
  });

  // Log detailed info for debugging
  console.log('Filtered data details:', {
    headers: newHeaders,
    headerCount: newHeaders.length,
    firstRow: processedRows.length > 1 ? processedRows[1].slice(0, 10) : [],
    dealerCount: new Set(Array.from(dealerYearMonthMap.values()).map(e => e.dealer)).size,
    totalRows: processedRows.length,
    kpiCount: kpiAnalysis.length,
    emptyRows: processedRows.filter(row => 
      row.slice(3).every(cell => !cell)).length - 1 // Subtract 1 for header row
  });

  return {
    headers: newHeaders,
    rows: processedRows
  };
};

// Function to create a CSV string from the filtered data
const createFilteredCSVString = (filteredData: string[][]): string => {
  if (!filteredData || filteredData.length === 0) return '';
  return filteredData.map(row => row.join(',')).join('\n');
};

// Function to create a new CSVFile from filtered data
const createFilteredCSVFile = (originalFile: CSVFile, filteredData: string[][]): CSVFile => {
  return {
    ...originalFile,
    columns: filteredData[0],
    rows: filteredData.slice(1),
    previewData: filteredData.slice(1, 6), // First 5 rows for preview
    data: filteredData
  };
};

const CSVUploadWithQuestionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    uploadedFile, 
    setUploadedFile, 
    validationResults, 
    setValidationResults,
    validateCurrentFile, 
    currentStep, 
    setCurrentStep,
    setCSVData 
  } = useCSVStore();
  
  // Get current user
  const { user } = useAuthStore();
  
  const [activeStep, setActiveStep] = useState<number>(UPLOAD);
  const [userQuestion, setUserQuestion] = useState('');
  const [userQuestions, setUserQuestions] = useState<UserQuestion[]>([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [questionError, setQuestionError] = useState<string | null>(null);
  const [fileRef, setFileRef] = useState<string | null>(null);
  const [aiMappingResult, setAiMappingResult] = useState<string | null>(null);
  const [isMapping, setIsMapping] = useState(false);
  const [mappingError, setMappingError] = useState<string | null>(null);
  const [kpiData, setKpiData] = useState<{ headers: string[], kpiData: string[][] }>({ headers: [], kpiData: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isIdentifyingKPIs, setIsIdentifyingKPIs] = useState(false);
  const [kpiAnalysis, setKpiAnalysis] = useState<KPIAnalysisObj[]>([]);
  const [filteredCSVData, setFilteredCSVData] = useState<string[][]>([]);
  const [filteredCSVFile, setFilteredCSVFile] = useState<CSVFile | null>(null);
  const [filteredCSVContent, setFilteredCSVContent] = useState<string>('');
  const [filteredDataError, setFilteredDataError] = useState<string | null>(null);
  const [mockAnalysisResult, setMockAnalysisResult] = useState<string | null>(null);
  const [isMockReportOpen, setIsMockReportOpen] = useState(false);
  const [isManuallyMapped, setIsManuallyMapped] = useState(false);
  const [usedKpiAnalysisFallback, setUsedKpiAnalysisFallback] = useState(false);

  // Function to fetch user's saved questions
  const fetchUserQuestions = async () => {
    if (!user?.email) return;
    
    setIsLoadingQuestions(true);
    setQuestionError(null);
    
    try {
      const questions = await UserQuestionService.getUserQuestionsByEmail(user.email);
      setUserQuestions(questions);
    } catch (error: any) {
      console.error('Failed to fetch user questions:', error);
      setQuestionError('Failed to load your saved questions');
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  // Fetch user questions when component mounts or user changes
  useEffect(() => {
    fetchUserQuestions();
  }, [user?.email]);

  // Mock analysis result with visualizations
  const MOCK_ANALYSIS_RESULT = `# Financial Analysis for Automotive Dealerships

## Executive Summary

Based on the financial data provided, I've identified several important trends and insights about the dealership's performance.

### Key Findings

- Overall gross profit margin is 24.3%, which is within industry benchmarks
- Service department shows strong performance with 72% margins
- New vehicle sales discount rates average 7.2%, with some outliers
- Used vehicle department shows declining inventory turnover

## Detailed Analysis by Department

### New Vehicle Sales Department

New vehicle sales have been consistently strong across all dealerships with an average profit margin of 18.5%. However, I noticed significant variation between dealerships:

1. **Elite Motors** consistently outperforms with a 23.7% margin
2. **Luxury Auto** has the highest average transaction value at $58,230
3. **Valley Autos** shows concerning discount rates averaging 12.3% (industry benchmark is 7-8%)

The following visualization shows the new vehicle profit margins by dealership:

\`\`\`visualization
{
  "type": "bar",
  "title": "New Vehicle Profit Margins by Dealership",
  "description": "This chart shows the profit margins for new vehicle sales across different dealerships.",
  "labels": ["Elite Motors", "Valley Autos", "Luxury Auto", "Metro Cars", "Highway Rides"],
  "datasets": [
    {
      "label": "Profit Margin %",
      "data": [23.7, 15.2, 19.8, 17.6, 16.2],
      "backgroundColor": "rgba(66, 133, 244, 0.7)"
    }
  ],
  "xAxisLabel": "Dealership",
  "yAxisLabel": "Margin (%)"
}
\`\`\`

### Used Vehicle Sales Department

The used vehicle department has shown mixed results with an overall lower performance than new vehicles. The average profit margin is 15.3%, but inventory turnover has decreased by 22% compared to last year.

Key insights for used vehicles:

- Average days to sell: 45 days (industry benchmark is 30-35 days)
- Reconditioning costs are 15% higher than benchmark
- Finance & Insurance attachment rate is strong at 82%

\`\`\`visualization
{
  "type": "line",
  "title": "Used Vehicle Monthly Sales Trend",
  "description": "Monthly sales volume for used vehicles over the past 6 months shows a declining trend.",
  "labels": ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
  "datasets": [
    {
      "label": "Units Sold",
      "data": [120, 132, 101, 94, 85, 77],
      "borderColor": "rgba(225, 95, 50, 0.8)",
      "backgroundColor": "rgba(225, 95, 50, 0.2)"
    }
  ],
  "xAxisLabel": "Month",
  "yAxisLabel": "Units Sold"
}
\`\`\`

### Service Department Performance

The service department is the strongest performing area with 72% gross profit margins. Customer retention rate is excellent at 68% and there's significant opportunity to increase capacity utilization.

Key service metrics:
- Technician efficiency: 87% (benchmark: 85%)
- Labor rate realization: 92%
- Parts markup average: 38%

\`\`\`visualization
{
  "type": "pie",
  "title": "Service Department Revenue Sources",
  "description": "Breakdown of revenue sources in the service department showing labor as the primary component.",
  "labels": ["Labor", "Parts", "Sublet", "Other"],
  "datasets": [
    {
      "label": "Revenue Distribution",
      "data": [55, 32, 8, 5],
      "backgroundColor": [
        "rgba(66, 133, 244, 0.7)",
        "rgba(52, 168, 83, 0.7)",
        "rgba(251, 188, 5, 0.7)",
        "rgba(234, 67, 53, 0.7)"
      ]
    }
  ]
}
\`\`\`

## Financial Health Indicators

Overall dealership financial health appears strong with good liquidity ratios and manageable debt levels.

### Key Financial Metrics:
- Current Ratio: 1.8 (industry average: 1.5)
- Quick Ratio: 1.2 (industry average: 1.0)
- Debt-to-Equity: 2.1 (industry average: 2.3)

\`\`\`visualization
{
  "type": "bar",
  "title": "Monthly Revenue by Department",
  "description": "Comparison of monthly revenue across different departments.",
  "labels": ["New Vehicles", "Used Vehicles", "Service", "Parts", "F&I"],
  "datasets": [
    {
      "label": "Revenue (thousands)",
      "data": [1250, 875, 340, 180, 220],
      "backgroundColor": [
        "rgba(52, 168, 83, 0.7)", 
        "rgba(66, 133, 244, 0.7)", 
        "rgba(251, 188, 5, 0.7)", 
        "rgba(234, 67, 53, 0.7)",
        "rgba(153, 102, 255, 0.7)"
      ]
    }
  ],
  "xAxisLabel": "Department",
  "yAxisLabel": "Revenue ($000s)"
}
\`\`\`

## Recommendations

Based on this analysis, I recommend the following actions:

1. **New Vehicles**: Implement a discount approval process at Valley Autos to bring discount rates in line with benchmarks
2. **Used Vehicles**: Review reconditioning processes to reduce costs and time to frontline
3. **Service**: Increase marketing for service specials to improve capacity utilization
4. **Overall**: Consider implementing a centralized inventory management system to improve turnover rates

\`\`\`visualization
{
  "type": "doughnut",
  "title": "Profit Distribution by Department",
  "description": "The percentage of total dealership profit contributed by each department.",
  "labels": ["New Vehicles", "Used Vehicles", "Service & Parts", "F&I"],
  "datasets": [
    {
      "label": "Profit Distribution",
      "data": [42, 18, 25, 15],
      "backgroundColor": [
        "rgba(66, 133, 244, 0.7)", 
        "rgba(234, 67, 53, 0.7)", 
        "rgba(52, 168, 83, 0.7)", 
        "rgba(251, 188, 5, 0.7)"
      ]
    }
  ]
}
\`\`\`

## Conclusion

The dealership group is performing well overall, with particularly strong results in service operations and new vehicle sales. By addressing the specific recommendations outlined above, there's potential to increase overall profitability by an estimated 12-15% in the next fiscal year.

I'll be happy to provide more detailed analysis on any specific area that requires further investigation.`;

  // Prompt suggestions for different analyses
  const QUESTION_SUGGESTIONS = [
    {
      title: "Rule Compliance Analysis",
      question: "Can you identify and highlight any data points that fall outside of standard financial norms and best practices?"
    },
    {
      title: "Top Performers Analysis",
      question: "What are the top 3 performing entities based on gross profit?"
    },
    {
      title: "Expense Trend Analysis",
      question: "Which expense categories show the most significant increase over the last 6 months?"
    },
    {
      title: "Department Performance Analysis",
      question: "Compare the performance across different departments or business units. Which areas show the strongest profitability?"
    }
  ];

  // Function to open the mock report
  const openMockReport = () => {
    setMockAnalysisResult(MOCK_ANALYSIS_RESULT);
    setIsMockReportOpen(true);
  };

  // Function to close the mock report
  const closeMockReport = () => {
    setIsMockReportOpen(false);
  };

  // Custom prompt for the questions feature
  const QUESTIONS_PROMPT = 
    "You are an expert financial analyst. " +
    "Please analyze the provided data and answer the following specific question from the user. " +
    "Present your analysis with clear markdown headings and keep explanations concise. " +
    
    "VISUALIZATION FORMAT: " +
    "When presenting visualizations, use the following Chart.js format: " +
    
    "SUPPORTED CHART TYPES: bar, line, area, pie, doughnut, scatter, bubble, radar, polarArea, histogram\n\n" +
    
    "For basic charts (bar, line, area, pie, doughnut, radar, polarArea):\n" +
    "```visualization\n" +
    "{\n" +
    "  \"type\": \"bar\",\n" +
    "  \"title\": \"Example Chart Title\",\n" +
    "  \"description\": \"Brief description\",\n" +
    "  \"labels\": [\"Label 1\", \"Label 2\", \"Label 3\"],\n" +
    "  \"datasets\": [{ \"label\": \"Dataset Name\", \"data\": [10, 20, 30] }],\n" +
    "  \"xAxisLabel\": \"X-Axis\",\n" +
    "  \"yAxisLabel\": \"Y-Axis\"\n" +
    "}\n" +
    "```\n\n" +
    
    "For scatter plots:\n" +
    "```visualization\n" +
    "{\n" +
    "  \"type\": \"scatter\",\n" +
    "  \"title\": \"Scatter Plot Title\",\n" +
    "  \"description\": \"Brief description\",\n" +
    "  \"labels\": [\"Point 1\", \"Point 2\", \"Point 3\"],\n" +
    "  \"datasets\": [{ \"label\": \"Dataset Name\", \"data\": [{\"x\": 10, \"y\": 20}, {\"x\": 15, \"y\": 25}] }],\n" +
    "  \"xAxisLabel\": \"X-Axis\",\n" +
    "  \"yAxisLabel\": \"Y-Axis\"\n" +
    "}\n" +
    "```\n\n" +
    
    "For bubble charts:\n" +
    "```visualization\n" +
    "{\n" +
    "  \"type\": \"bubble\",\n" +
    "  \"title\": \"Bubble Chart Title\",\n" +
    "  \"description\": \"Brief description\",\n" +
    "  \"labels\": [\"Bubble 1\", \"Bubble 2\", \"Bubble 3\"],\n" +
    "  \"datasets\": [{ \"label\": \"Dataset Name\", \"data\": [{\"x\": 10, \"y\": 20, \"r\": 5}, {\"x\": 15, \"y\": 25, \"r\": 8}] }],\n" +
    "  \"xAxisLabel\": \"X-Axis\",\n" +
    "  \"yAxisLabel\": \"Y-Axis\"\n" +
    "}\n" +
    "```\n\n" +
    
    "For histograms:\n" +
    "```visualization\n" +
    "{\n" +
    "  \"type\": \"histogram\",\n" +
    "  \"title\": \"Histogram Title\",\n" +
    "  \"description\": \"Brief description\",\n" +
    "  \"labels\": [],\n" +
    "  \"datasets\": [{ \"label\": \"Frequency\", \"data\": [1, 5, 10, 15, 20, 18, 12, 8, 3, 1] }],\n" +
    "  \"bins\": 10,\n" +
    "  \"xAxisLabel\": \"Value Ranges\",\n" +
    "  \"yAxisLabel\": \"Frequency\"\n" +
    "}\n" +
    "```\n\n" +
    
    "IMPORTANT: \n" +
    "- For scatter and bubble plots, always provide a labels array with meaningful names for each data point\n" +
    "- For bubble charts, use x, y, r format where r is the bubble radius\n" +
    "- For histograms, provide raw data and optionally specify bins count\n" +
    "- Use 'area' type for filled line charts, 'polarArea' for polar area charts\n\n" +
    "CRITICAL JSON FORMAT REQUIREMENTS:\n" +
    "- ALL JSON objects MUST be complete with matching brackets and braces\n" +
    "- Always include closing brackets ']' for arrays and closing braces '}' for objects\n" +
    "- For histograms, always include the 'labels' property (can be empty array: [])\n" +
    "- Double-check that all JSON is valid and properly formatted before returning\n\n";

  // Skip validation step for performance
  // useEffect(() => {
  //   if (activeStep === VALIDATE && uploadedFile && !validationResults) {
  //     validateCurrentFile();
  //   }
  // }, [activeStep, uploadedFile, validationResults, validateCurrentFile]);

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const goToNextStep = () => {
    if (activeStep < VALIDATE) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (activeStep > UPLOAD) {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleStartOver = () => {
    setUploadedFile(null);
    setValidationResults(null);
    setActiveStep(UPLOAD);
  };

  // Update the parseKPIAnalysis function
  const parseKPIAnalysis = (response: string): KPIAnalysisObj[] => {
    console.log('Raw KPI analysis response:', response);
    
    try {
      // Try to extract a valid JSON array from the response
      const jsonMatch = response.match(/\[[\s\S]*\]/);
      if (!jsonMatch) {
        console.error('No JSON array found in response');
        throw new Error('Invalid response format: No JSON array found');
      }
      
      const jsonStr = jsonMatch[0];
      console.log('Extracted JSON string:', jsonStr);
      
      // Parse the JSON array
      const kpiNames = JSON.parse(jsonStr);
      
      // Validate that we got an array of strings
      if (!Array.isArray(kpiNames)) {
        console.error('Response is not an array:', kpiNames);
        throw new Error('Invalid response format: Expected an array of KPI names');
      }
      
      // Validate that we got a reasonable number of KPIs (between 10 and 200)
      if (kpiNames.length < 10) {
        console.error(`Too few KPIs: got ${kpiNames.length}`);
        throw new Error(`Invalid response: Too few KPIs (${kpiNames.length}). Need at least 10 KPIs.`);
      }
      
      if (kpiNames.length > 200) {
        console.warn(`Too many KPIs: got ${kpiNames.length}, truncating to 200`);
        kpiNames.splice(200);
      }
      
      // Validate that all items are strings
      if (!kpiNames.every(name => typeof name === 'string')) {
        console.error('Response contains non-string values:', kpiNames);
        throw new Error('Invalid response format: All KPIs must be strings');
      }
      
      // Convert the array of names into KPIAnalysis objects
      return kpiNames.map(kpi => ({
        code: kpi,
        description: '', // Empty string since we only requested names
      }));
    } catch (error) {
      console.error('Error parsing KPI analysis:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to parse KPI analysis: ${error.message}`);
      }
      throw error;
    }
  };

  const handleMapWithAI = async () => {
    if (!uploadedFile?.data) return;
    setIsMapping(true);
    setMappingError(null);
    setAiMappingResult(null);
    setKpiAnalysis([]);
    setFilteredCSVData([]);
    setFilteredCSVFile(null);
    setFilteredCSVContent(''); // Clear any existing filtered content
    setIsManuallyMapped(false); // This is an AI-based mapping, not manual
    setUsedKpiAnalysisFallback(false); // Reset fallback indicator
    
    try {
      // Extract KPI data from the CSV
      const { headers: kpiHeaders, kpiData } = csvService.extractKPIData(uploadedFile.data);
      
      if (kpiData.length === 0) {
        throw new Error('No KPI data found in the CSV file. Please identify KPIs first.');
      }

      // Create a CSV string from the KPI data
      const kpiCsvContent = [
        kpiHeaders.join(','),
        ...kpiData.map(row => row.join(','))
      ].join('\n');

      // Create a more focused prompt for KPI analysis that requests JSON output
      const kpiAnalysisPrompt = `Analyze the CSV data and return a JSON array of at least 200 important KPI columns.

FORMAT: [{"code": "column1", "description": "Gross Profit"}, ...]

RULES:
1. Return ONLY the JSON array, no other text
2. Use EXACT column names from headers as 'code'
3. 'description' should be a simple, human-friendly name for the KPI
4. Include at least 200 KPIs where possible, but never less than 10
5. Keep response minimal - just the array

Data to analyze:
\`\`\`csv
${kpiCsvContent}
\`\`\`
`;

      // Use an extremely simple filename that is guaranteed to be valid
      const safeFilename = 'data';

      // Function to make the analysis request with different models
      const makeAnalysisRequest = async (modelId: string) => {
        console.log(`Making KPI analysis request with model: ${modelId}`);
        
        // Create request body with appropriate parameters for each model
        const requestBody: any = {
          prompt: kpiAnalysisPrompt,
          modelId: modelId,
          data: {
            content: kpiCsvContent,
            format: 'csv',
            fileName: safeFilename
          },
          requestType: 'kpi_analysis' // Specific signal for KPI analysis to exclude system prompt
        };
        
        // Add max_tokens parameter for Claude 3.7 Sonnet
        if (modelId === CLAUDE_37_SONNET_MODEL_ID) {
          requestBody.inference_config = {
            max_tokens: 400000 // Configure Claude 3.7 to use up to 400k tokens for larger outputs
          };
        }
        
        const response = await fetch(`${API_BASE_URL}/bedrock/analyze/data`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Analysis failed');
        }

        return await response.json();
      };

      let data;
      try {
        // First try with Claude 4.0 Sonnet
        data = await makeAnalysisRequest(CLAUDE_45_SONNET_MODEL_ID);
      } catch (initialError: any) {
        // Check if the error is due to token limits
        const errorMessage = initialError.message || '';
        const isTokenLimitError = 
          errorMessage.includes('Too many tokens') || 
          errorMessage.includes('token limit') ||
          errorMessage.includes('token rate limit');

        if (isTokenLimitError) {
          console.log('Token limit exceeded, falling back to Claude 3.7 Sonnet model for KPI analysis');
          setUsedKpiAnalysisFallback(true);
          
          try {
            // Retry with Claude 3.7 Sonnet
            data = await makeAnalysisRequest(CLAUDE_37_SONNET_MODEL_ID);
          } catch (fallbackError: any) {
            console.error('Fallback KPI analysis failed:', fallbackError);
            throw new Error(`KPI analysis failed with both models. Claude 3.7 error: ${fallbackError.message}`);
          }
        } else {
          // If it's not a token limit error, rethrow the original error
          throw initialError;
        }
      }
      
      // Log the raw response for debugging
      console.log('Raw AI Response:', {
        responseLength: data.result.length,
        first100Chars: data.result.substring(0, 100),
        last100Chars: data.result.substring(data.result.length - 100),
        containsJsonArray: data.result.includes('[') && data.result.includes(']'),
        fullResponse: data.result
      });

      // Parse the JSON response
      let parsedKPIs: KPIAnalysisObj[] = [];
      try {
        // Try to extract a valid JSON array from the response
        let jsonStr = data.result;
        // If the response contains any text before or after the array, try to extract just the array
        const arrayMatch = data.result.match(/\[\s*{[\s\S]*?}\s*\]/);
        if (arrayMatch) {
          jsonStr = arrayMatch[0];
        }
        try {
          // Parse the array of KPI objects
          const kpiObjs: any[] = JSON.parse(jsonStr);
          // Validate that we have an array of objects with code and description
          parsedKPIs = kpiObjs.filter(obj => obj && typeof obj.code === 'string' && typeof obj.description === 'string');
          if (parsedKPIs.length < 10) {
            throw new Error(`Incomplete response: received only ${parsedKPIs.length} KPIs. Need at least 10 KPIs.`);
          }
        } catch (jsonError) {
          throw new Error('Failed to parse KPI analysis: ' + (jsonError as Error).message);
        }
      } catch (err) {
        throw new Error('Failed to parse KPI analysis: ' + (err as Error).message);
      }
      
      // Add a message about fallback if used
      let resultText = data.result;
      if (usedKpiAnalysisFallback) {
        resultText = `NOTE: The Claude 3.7 model could not process your request due to token limits. Automatically retried with Claude 3.5 Sonnet model.\n\n${resultText}`;
      }
      
      setAiMappingResult(resultText);
      setKpiAnalysis(parsedKPIs);
      // Always create filtered data after AI response is set, using the original uploaded CSV
      if (uploadedFile && uploadedFile.data && parsedKPIs.length > 0) {
        const filteredData = createFilteredCSVData(uploadedFile.data, parsedKPIs);
        setFilteredCSVData(filteredData.rows);
        // Create and store the filtered CSV file
        const newFilteredFile = createFilteredCSVFile(uploadedFile, filteredData.rows);
        setFilteredCSVFile(newFilteredFile);
        // Create and store the filtered CSV content
        const newFilteredContent = createFilteredCSVString(filteredData.rows);
        setFilteredCSVContent(newFilteredContent);
        
        // Extract and store unique dealers and KPIs
        await extractAndStoreUniqueData(filteredData.rows, parsedKPIs);
      } else {
        setFilteredCSVData([]);
        setFilteredCSVFile(null);
        setFilteredCSVContent('');
      }
      
      console.log('Extracted KPIs:', parsedKPIs);
    } catch (err: any) {
      console.error('Error during KPI analysis:', err);
      setMappingError(err.message || 'An error occurred during analysis');
    } finally {
      setIsMapping(false);
    }
  };

  const handleIdentifyKPIs = async () => {
    if (!uploadedFile?.data) return;
    setIsIdentifyingKPIs(true);
    setError(null);
    
    try {
      // Extract KPIs from the uploaded data
      const kpiInfo = csvService.extractKPIData(uploadedFile.data);
      setKpiData(kpiInfo);
      
      if (kpiInfo.headers.length === 0) {
        throw new Error('No KPI columns found in the data');
      }
    } catch (err: any) {
      setError(err.message || 'Error identifying KPIs');
    } finally {
      setIsIdentifyingKPIs(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const csvFile = await csvService.parseCSV(file);
      setUploadedFile(csvFile);
      
      // Extract KPIs from the uploaded data
      const kpiInfo = csvService.extractKPIData(csvFile.data || []);
      setKpiData(kpiInfo);
      
      // Set CSV data in store
      setCSVData(csvFile.data || []);
      
          // Skip validation for performance
      
      // Move to next step
      setCurrentStep(UploadStep.REVIEW_DATA);
    } catch (err: any) {
      setError(err.message || 'Error processing file');
    } finally {
      setIsLoading(false);
    }
  };

  // Skip validation step for performance
  // useEffect(() => {
  //   if (activeStep === VALIDATE && filteredCSVFile && !validationResults) {
  //     // Update the uploaded file in the store with the filtered version
  //     setUploadedFile(filteredCSVFile);
  //     // Then validate the current file
  //     validateCurrentFile();
  //   }
  // }, [activeStep, filteredCSVFile, validationResults, validateCurrentFile, setUploadedFile]);

  // Add a useEffect to always generate filtered data when both uploadedFile.data and kpiAnalysis are available
  useEffect(() => {
    console.log('[FilteredDataEffect] uploadedFile:', uploadedFile);
    console.log('[FilteredDataEffect] uploadedFile.data:', uploadedFile && uploadedFile.data ? uploadedFile.data.slice(0, 2) : null);
    console.log('[FilteredDataEffect] kpiAnalysis:', kpiAnalysis);
    setFilteredDataError(null);
    if (uploadedFile && uploadedFile.data && kpiAnalysis && kpiAnalysis.length > 0) {
      try {
        const filteredData = createFilteredCSVData(uploadedFile.data, kpiAnalysis);
        setFilteredCSVData(filteredData.rows);
        // Create and store the filtered CSV file
        const newFilteredFile = createFilteredCSVFile(uploadedFile, filteredData.rows);
        setFilteredCSVFile(newFilteredFile);
        // Create and store the filtered CSV content
        const newFilteredContent = createFilteredCSVString(filteredData.rows);
        setFilteredCSVContent(newFilteredContent);
        setFilteredDataError(null);
        console.log('[FilteredDataEffect] Filtered data generated:', filteredData.rows.slice(0, 2));
        
        // Extract and store unique dealers and KPIs
        extractAndStoreUniqueData(filteredData.rows, kpiAnalysis);
      } catch (err: any) {
        setFilteredCSVData([]);
        setFilteredCSVFile(null);
        setFilteredCSVContent('');
        setFilteredDataError('Failed to generate filtered data: ' + (err.message || err.toString()));
        console.error('[FilteredDataEffect] Error generating filtered data:', err);
      }
    } else {
      setFilteredCSVData([]);
      setFilteredCSVFile(null);
      setFilteredCSVContent('');
      if (!uploadedFile || !uploadedFile.data) {
        setFilteredDataError('No uploaded file data available.');
      } else if (!kpiAnalysis || kpiAnalysis.length === 0) {
        setFilteredDataError('No KPI analysis available.');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFile, kpiAnalysis]);

  // Add a new function to handle manual KPI mapping


  const renderStepContent = () => {
    switch (activeStep) {
      case UPLOAD:
        return (
          <div className="upload-step">
            <h2>Upload CSV File</h2>
            <p>
              Upload your financial data CSV file for validation and ask specific questions about your data.
              We'll help you gain insights and ensure your data meets the required format.
            </p>
            <FileUploader onFileProcessed={goToNextStep} onFileUploaded={setFileRef} />
          </div>
        );
      case PREVIEW:
        return (
          <div className="upload-step">
            <div className="step-header">
              <h2>Preview Data</h2>
              <button className="back-button" onClick={goToPreviousStep}>
                Back
              </button>
            </div>
            <p>
              Review your uploaded data before proceeding with validation. Make sure the data appears 
              correct and properly formatted.
            </p>
            {uploadedFile ? (
              <>
                <CSVPreview file={uploadedFile} />
                <div className="validation-actions">
                  <button className="primary-button" onClick={goToNextStep}>
                    Map KPI's with AI
                  </button>
                </div>
              </>
            ) : (
              <div className="empty-state">No file uploaded. Please go back and upload a file.</div>
            )}
          </div>
        );
      case MAP:
        return (
          <div className="upload-step">
            <div className="step-header">
              <h2>Map KPI's with AI</h2>
              <button className="back-button" onClick={goToPreviousStep}>
                Back
              </button>
            </div>
            <p>
              First identify the KPIs in your data, then use AI to analyze them and provide insights.
            </p>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
              <button 
                className="secondary-button" 
                onClick={handleIdentifyKPIs} 
                disabled={isIdentifyingKPIs || !uploadedFile?.data}
              >
                {isIdentifyingKPIs ? 'Identifying KPIs...' : 'Identify KPIs'}
              </button>
              <button 
                className="primary-button" 
                onClick={handleMapWithAI} 
                disabled={isMapping || !kpiData.headers.length}
              >
                                  {isMapping ? 'Analyzing KPIs...' : 'Analyse KPI\'s with Lumina AI'}
              </button>
            </div>
            {mappingError && <div className="error-message">{mappingError}</div>}
            {error && <div className="error-message">{error}</div>}
            
            {/* Only show KPI Table if KPIs were identified AND we're not currently analyzing */}
            {kpiData.headers.length > 0 && !isMapping && !aiMappingResult && (
              <div style={{ marginTop: '24px' }}>
                <KPITable 
                  headers={kpiData.headers}
                  kpiData={kpiData.kpiData}
                />
              </div>
            )}
            
            {aiMappingResult && (
              <div className="mapping-result" style={{ marginTop: 24 }}>
                <h4>KPI Analysis Complete!</h4>
                {usedKpiAnalysisFallback && (
                  <div className="error-message" style={{ color: '#b8860b', marginTop: 8, marginBottom: 16 }}>
                    The Claude 3.7 model could not process your request due to token limits. Automatically retried with Claude 3.5 Sonnet model.
                  </div>
                )}
                {kpiAnalysis.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    {/* Filtered Data Preview with logging and error display */}
                    {filteredDataError && (
                      <div className="error-message" style={{ marginTop: 16 }}>{filteredDataError}</div>
                    )}
                    {filteredCSVFile && (
                      <div style={{ marginTop: 24 }}>
                        <h4>Filtered Data Preview:</h4>
                        {filteredCSVFile.columns && Array.isArray(filteredCSVFile.columns) ? (
                          <p>Created new CSV file with {filteredCSVFile.columns.length} columns (including {kpiAnalysis.length} KPI columns)</p>
                        ) : null}
                        {filteredCSVFile && Array.isArray(filteredCSVFile.columns) ? (
                          <CSVPreview file={filteredCSVFile} />
                        ) : null}
                        {filteredCSVData.length === 0 && !filteredDataError && (
                          <div style={{ marginTop: 12, color: '#888' }}>No filtered data available for the selected KPIs.</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className="validation-actions">
                  <button className="primary-button" onClick={goToNextStep} disabled={!aiMappingResult || isMapping}>
                    Proceed to Analysis
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      case VALIDATE:
        // Build the full prompt for the AI analysis, without the KPI mapping section
        const fullPrompt = QUESTIONS_PROMPT + "Please analyze the data and answer the following question: ";
        return (
          <div className="upload-step">
            <div className="step-header">
              <h2>CSV Analysis</h2>
              <button className="back-button" onClick={goToPreviousStep}>
                Back
              </button>
            </div>
            <p>
              Ready to ask questions about your data.
            </p>
            {uploadedFile ? (
              <>
                {/* Add confirmation about filtered data */}
                {filteredCSVContent && (
                  <div className="success-message" style={{ 
                    backgroundColor: '#e6f7e6', 
                    border: '1px solid #a3d9a3', 
                    borderRadius: '4px', 
                    padding: '10px 15px',
                    marginTop: '16px',
                    fontSize: '14px' 
                  }}>
                    <p style={{ margin: '0', display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontWeight: 'bold', marginRight: '8px' }}>✓</span>
                      Your data is ready for Analysis. You have {filteredCSVFile?.columns?.length || 0} columns, {filteredCSVData.length - 1} rows and {new Set(filteredCSVData.slice(1).map(row => row[0])).size} unique dealers.
                    </p>
                  </div>
                )}



                <div className="question-section">
                  <h3>Ask Questions About Your Data</h3>
                  <p>
                    Get AI-powered insights by asking specific questions about your financial data.
                  </p>
                  <div className="question-input-container">
                    <textarea
                      className="question-input"
                      placeholder="Ask questions about your data..."
                      value={userQuestion}
                      onChange={(e) => setUserQuestion(e.target.value)}
                      rows={6}
                    />
                  </div>
                  {!userQuestion ? (
                    <div className="prompt-suggestion">
                      {isLoadingQuestions ? (
                        <div>Loading your saved questions...</div>
                      ) : questionError ? (
                        <div style={{ color: '#e76f51' }}>{questionError}</div>
                      ) : userQuestions.length > 0 ? (
                        <>
                          <h4>Your Saved Questions:</h4>
                          <ul className="suggestion-list">
                            {userQuestions.map((question, index) => (
                              <li key={question.id || index} onClick={() => setUserQuestion(question.questionText)}>
                                {question.questionText}
                              </li>
                            ))}
                          </ul>
                        </>
                      ) : (
                        <>
                          <h4>Suggested Questions:</h4>
                          <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                            You don't have any saved questions yet. Here are some suggestions to get you started:
                          </p>
                          <ul className="suggestion-list">
                            {QUESTION_SUGGESTIONS.map((suggestion, index) => (
                              <li key={index} onClick={() => setUserQuestion(suggestion.question)}>
                                {suggestion.title}
                              </li>
                            ))}
                          </ul>
                          <p style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}>
                            💡 Tip: You can save frequently used questions in the <strong>User Questions</strong> section for quick access.
                          </p>
                        </>
                      )}
                    </div>
                  ) : null}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <CustomAIAnalysisButton
                      prompt={userQuestion ? `${fullPrompt} "${userQuestion}"` : fullPrompt}
                      fileRef={fileRef}
                      modelId={CLAUDE_45_SONNET_MODEL_ID}
                      disabled={!userQuestion || (!filteredCSVContent && !uploadedFile?.data)}
                      filteredContent={filteredCSVContent || (uploadedFile?.data || []).map(row => row.join(',')).join('\n')}
                      isManuallyMapped={isManuallyMapped}
                    />
                  </div>

                </div>
                
                {/* Completion Actions */}
                <div className="completion-actions" style={{ marginTop: '32px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                  <h4>Analysis Complete!</h4>
                  <p>Your CSV file has been successfully processed and analyzed. You can continue asking questions or return to the dashboard.</p>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '16px' }}>
                    <button className="secondary-button" onClick={handleStartOver}>
                      Upload Another File
                    </button>
                    <button className="primary-button" onClick={goToDashboard}>
                      Return to Dashboard
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-state">No file uploaded. Please go back and upload a file.</div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="breadcrumbs">
            <button className="breadcrumb-link" onClick={goToDashboard}>
              Dashboard
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Upload CSV with Questions</span>
          </div>

          <div className="csv-upload-container">
            <div className="stepper">
              <div className={`step ${activeStep === UPLOAD ? 'active' : ''} ${activeStep > UPLOAD ? 'completed' : ''}`}>
                <div className="step-number">{activeStep > UPLOAD ? '✓' : '1'}</div>
                <div className="step-label">Upload</div>
              </div>
              <div className={`step ${activeStep === PREVIEW ? 'active' : ''} ${activeStep > PREVIEW ? 'completed' : ''}`}>
                <div className="step-number">{activeStep > PREVIEW ? '✓' : '2'}</div>
                <div className="step-label">Preview</div>
              </div>
              <div className={`step ${activeStep === MAP ? 'active' : ''} ${activeStep > MAP ? 'completed' : ''}`}>
                <div className="step-number">{activeStep > MAP ? '✓' : '3'}</div>
                <div className="step-label">Map KPI's with AI</div>
              </div>
              <div className={`step ${activeStep === VALIDATE ? 'active' : ''} ${activeStep > VALIDATE ? 'completed' : ''}`}>
                <div className="step-number">{activeStep > VALIDATE ? '✓' : '4'}</div>
                <div className="step-label">Analysis</div>
              </div>
            </div>

            {renderStepContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSVUploadWithQuestionsPage; 